import React, { useState } from 'react';
import { brandGradients, brandTextGradients } from '../theme/palette';

const Services = () => {
  const [activeCategory, setActiveCategory] = useState('banking');

  const serviceCategories = {
    banking: {
      title: 'Banking & Financial Verification',
      icon: 'fas fa-university',
      color: 'from-blue-500 to-blue-600',
      services: [
        'Credit Verification',
        'Address Verification', 
        'Salaried Verification',
        'Business Verification',
        'Vendor Background Verification',
        'Due Diligence'
      ]
    },
    employment: {
      title: 'Employee Background Checks',
      icon: 'fas fa-user-tie',
      color: 'from-green-500 to-green-600', 
      services: [
        'Current Address Verification',
        'Permanent Address Verification',
        'Previous/Current Employment Verification',
        'Reference Checks',
        'Educational Background Verification',
        'Criminal Background Check'
      ]
    },
    premarital: {
      title: 'Premarital Verification',
      icon: 'fas fa-heart',
      color: 'from-purple-500 to-purple-600',
      services: [
        'Family Member Verification',
        'Address Verification',
        'Neighborhood Confirmation',
        'Current Employment Verification',
        'Previous Employment Verification', 
        'Discreet Verification with Colleagues'
      ]
    },
    investigation: {
      title: 'Investigation Services',
      icon: 'fas fa-search',
      color: 'from-indigo-500 to-indigo-600',
      services: [
        'Profile Investigation',
        'Vehicle Seizure Investigation',
        'Professional Background Check',
        'Personal Profile Analysis',
        'Asset Verification',
        'Fraud Investigation'
      ]
    }
  };

  return (
    <section id="services" className="py-20 lg:py-16 bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Cpath d='M50 50m-20 0a20 20 0 1 1 40 0a20 20 0 1 1 -40 0'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      }}></div>

  <div className="container relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 animate-on-scroll">
          <span className="text-[#00a4f4] text-xl font-bold"style={{ color: '#ff70a6' }}>
            Our Services
          </span>
          <h2 className="text-4xl lg:text-6xl font-black text-gray-800 mt-4 mb-8 leading-tight">
            Comprehensive Verification
            <span className="block text-blue-600">& Investigation Services</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            We provide a complete range of professional verification and investigation services 
            across banking, employment, premarital, and specialized investigation sectors.
          </p>
        </div>

        {/* Service Category Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-16 animate-on-scroll">
          {Object.entries(serviceCategories).map(([key, category]) => (
            <button
              key={key}
              onClick={() => setActiveCategory(key)}
              className={`group relative px-6 py-4 rounded-2xl font-semibold transition-all duration-300 ${
                activeCategory === key
                  ? `bg-gradient-to-r ${category.color} text-white shadow-xl transform scale-105`
                  : 'bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-600 shadow-1xl hover:shadow-1xl'
              }`}
            >
              <span className="flex items-center">
                <i className={`${category.icon} mr-3 text-lg`}></i>
                {category.title}
              </span>
            </button>
          ))}
        </div>

        {/* Active Category Services */}
        <div className="animate-on-scroll">
          <div className="text-center mb-12">
            <div className={`inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r ${serviceCategories[activeCategory].color} rounded-2xl mb-6 shadow-xl`}>
              <i className={`${serviceCategories[activeCategory].icon} text-white text-3xl`}></i>
            </div>
            <h3 className="text-3xl font-bold text-gray-800 mb-4">
              {serviceCategories[activeCategory].title}
            </h3>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {serviceCategories[activeCategory].services.map((service, index) => (
              <div 
                key={index}
                className="group p-6 bg-white rounded-2xl shadow-1xl hover:shadow-1xl transition-all duration-500 border border-gray-100 hover:border-blue-200"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 bg-gradient-to-r ${serviceCategories[activeCategory].color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                    <i className="fas fa-check text-white text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300">
                      {service}
                    </h4>
                    <p className="text-gray-600 text-sm">Professional verification service</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Service Features */}
        <div className="mt-20 animate-on-scroll">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl">
              <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="fas fa-shield-alt text-white text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-800 mb-4">100% Confidential</h4>
              <p className="text-gray-600">All verification processes maintain highest degree of confidentiality and security.</p>
            </div>

            <div className="text-center p-8 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl">
              <div className="w-16 h-16 bg-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="fas fa-clock text-white text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-800 mb-4">Timely Delivery</h4>
              <p className="text-gray-600">Reports delivered within committed timeframe with excellent time management.</p>
            </div>

            <div className="text-center p-8 bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl">
              <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="fas fa-file-alt text-white text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-800 mb-4">Detailed Reports</h4>
              <p className="text-gray-600">Comprehensive, understandable reports in prescribed format with in-depth analysis.</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-20 text-center animate-on-scroll">
          <div className="rounded-3xl p-12 text-white" style={{ background: brandGradients.panel }}>
            <h3 className="text-3xl lg:text-4xl font-bold mb-4">
              Need Professional Verification Services?
            </h3>
            <p className="text-xl mb-8 opacity-90">
              Contact us today for confidential, timely, and accurate verification solutions.
            </p>
            <button className="bg-white text-blue-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-xl">
              <i className="fas fa-phone mr-2"></i>
              Get Free Consultation
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;